import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, TrendingUp, TrendingDown, Users, MessageSquare, Clock, Award } from 'lucide-react';
import { VendorKPIs } from '@/hooks/useVendorPerformance';
import { cn } from '@/lib/utils';

interface VendorKPICardsProps {
  data: VendorKPIs | undefined;
  isLoading: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function formatTime(hours: number): string {
  if (hours < 48) {
    return `${Math.round(hours)} hours`;
  }
  return `${(hours / 24).toFixed(1)} days`;
}

function TrendIndicator({ value, inverted = false }: { value: number; inverted?: boolean }) {
  const isPositive = inverted ? value < 0 : value > 0;
  const isNegative = inverted ? value > 0 : value < 0;
  
  if (Math.abs(value) < 0.1) return null;

  return (
    <div className={cn(
      'flex items-center gap-1 text-sm',
      isPositive && 'text-green-600 dark:text-green-400',
      isNegative && 'text-red-600 dark:text-red-400'
    )}>
      {isPositive ? (
        <TrendingUp className="h-4 w-4" />
      ) : (
        <TrendingDown className="h-4 w-4" />
      )}
      <span>{Math.abs(value).toFixed(1)}%</span>
    </div>
  );
}

export function VendorKPICards({ data, isLoading }: VendorKPICardsProps) {
  if (isLoading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Loading...</CardTitle>
            </CardHeader>
            <CardContent className="flex items-center justify-center h-16">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  const kpis = data || {
    activeVendors: 0,
    newVendors: 0,
    activeVendorsTrend: 0,
    avgBidsPerJob: 0,
    totalBids: 0,
    avgBidsPerJobTrend: 0,
    avgResponseTimeHours: 0,
    medianResponseTimeHours: 0,
    avgResponseTimeTrend: 0,
    diversitySpendPercent: 0,
    diversitySpendAmount: 0,
    diversitySpendTrend: 0,
  };

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      {/* Active Vendors */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Active Vendors</CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline justify-between">
            <div className="text-3xl font-bold">{kpis.activeVendors}</div>
            <TrendIndicator value={kpis.activeVendorsTrend} />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {kpis.newVendors} new vendors this period
          </p>
        </CardContent>
      </Card>

      {/* Avg Bids per Job */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Avg Bids per Job</CardTitle>
          <MessageSquare className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline justify-between">
            <div className="text-3xl font-bold">{kpis.avgBidsPerJob.toFixed(1)}</div>
            <TrendIndicator value={kpis.avgBidsPerJobTrend} />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            From {kpis.totalBids} total bids
          </p>
        </CardContent>
      </Card>

      {/* Average Response Time */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Avg Response Time</CardTitle>
          <Clock className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline justify-between">
            <div className="text-3xl font-bold">{formatTime(kpis.avgResponseTimeHours)}</div>
            <TrendIndicator value={kpis.avgResponseTimeTrend} inverted />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Median: {formatTime(kpis.medianResponseTimeHours)}
          </p>
        </CardContent>
      </Card>

      {/* Diversity Spend */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">Diversity Spend</CardTitle>
          <Award className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="flex items-baseline justify-between">
            <div className="text-3xl font-bold">{kpis.diversitySpendPercent.toFixed(0)}%</div>
            <TrendIndicator value={kpis.diversitySpendTrend} />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {formatCurrency(kpis.diversitySpendAmount)} with certified vendors
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
